import React, { useContext, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { 
    Box, 
    CssBaseline, 
    AppBar as MuiAppBar, 
    Toolbar, 
    Typography, 
    IconButton, 
    Drawer, 
    List, 
    Avatar,
    Menu,
    MenuItem,
    Divider,
    ListItemIcon,
    Container,
    useMediaQuery
} from '@mui/material';

// Iconos
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faShieldAlt } from '@fortawesome/free-solid-svg-icons';

// Contextos y Utilidades
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthContext from '../../navigation/context/authContext';
import UserContext from '../../navigation/context/userContext';
import { imagesURL } from '../../apiRoutes/apiBase';

// Componentes
import AdminListItems from './AdminListItems';
import Copyright from '../../components/copyRight';

// Assets
import logoQUD from '../../assets/images/logos/logoQUD.png';
import en_language_img from '../../assets/images/countries/usa.png';
import es_language_img from '../../assets/images/countries/spain.png';

const DRAWER_WIDTH = 260; // Ajustado a 260px para que coincida con tu ClientLayout

// =====================================================================
// ESTILOS ANIMADOS (STYLED COMPONENTS)
// =====================================================================

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${DRAWER_WIDTH}px`,
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
    backgroundColor: '#ffffff', // Fondo blanco limpio para el Super Admin
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column'
  }),
);

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  backgroundColor: '#ffffff',
  color: theme.palette.text.primary,
  boxShadow: 'none',
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${DRAWER_WIDTH}px)`,
    marginLeft: DRAWER_WIDTH,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

export default function AdminLayout(props) {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const history = useHistory();
    const location = useLocation();
    const isMobile = useMediaQuery(theme.breakpoints.down('md')); 
    
    const { userData } = useContext(UserContext);
    const { signOut } = useContext(AuthContext);

    // El drawer inicia abierto en PC y cerrado en móviles
    const [open, setOpen] = useState(!isMobile); 
    const [anchorEl, setAnchorEl] = useState(null);

    const handleDrawerOpen = () => setOpen(true);
    const handleDrawerClose = () => setOpen(false);
    const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);

    const handleSignOut = () => {
        handleMenuClose();
        history.replace('/');
        signOut();
    };

    const handleLanguage = () => {
        i18n.changeLanguage(i18n.language === 'es' ? 'en' : 'es');
        handleMenuClose();
    };

    const redirect = (route) => {
        history.push(route);
        handleMenuClose();
    };

    const getPageTitle = () => {
        const path = location.pathname;
        if (path.includes('/admin/dashboard')) return 'Visión General';
        if (path.includes('/admin/billing')) return 'Suscripciones y Facturación';
        if (path.includes('/admin/plans')) return 'Configuración de Planes';
        if (path.includes('/admin/qud-pass')) return 'Gestión QUD Pass';
        if (path.includes('/admin/events')) return 'Gestor de Eventos';
        if (path.includes('/profile')) return 'Perfil Administrativo';
        return 'Super Admin';
    };

    // Si el usuario da clic en un item en versión móvil, escondemos el drawer
    const handleMenuItemClick = () => {
        if (isMobile) {
            setOpen(false);
        }
    };

    const userInitial = userData.user.name ? userData.user.name.charAt(0).toUpperCase() : 'Q';

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            
            {/* --- 1. APP BAR (Header Superior) --- */}
            <AppBar position="fixed" open={open}>
                <Toolbar sx={{ borderBottom: '1px solid #e0e0e0' }}> 
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        onClick={handleDrawerOpen}
                        edge="start"
                        sx={{ mr: 2, color: '#333', ...(open && { display: 'none' }) }}
                    >
                        <MenuIcon />
                    </IconButton>

                    <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 700, color: '#1a1a1a' }}>
                        {getPageTitle()}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={handleMenuOpen} sx={{ p: 0, ml: 1 }}>
                            <Avatar 
                                sx={{ 
                                    bgcolor: theme.palette.primary.main,
                                    color: '#FFF',
                                    width: 36, 
                                    height: 36, 
                                    fontSize: '1rem', 
                                    fontWeight: 'bold',
                                    border: '1px solid #e0e0e0'
                                }}
                                src={userData.user.profile_image ? `${imagesURL}users/${userData.user.profile_image}` : null}
                            >
                                {userInitial}
                            </Avatar>
                        </IconButton>

                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                            PaperProps={{
                                elevation: 0,
                                sx: {
                                    overflow: 'visible',
                                    filter: 'drop-shadow(0px 4px 20px rgba(0,0,0,0.08))',
                                    mt: 1.5,
                                    borderRadius: 3,
                                    minWidth: 220,
                                    border: '1px solid #e0e0e0'
                                },
                            }}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        >
                            <Box sx={{ px: 2, py: 1.5 }}>
                                <Typography variant="subtitle2" fontWeight="bold">
                                    {userData.user.name} {userData.user.last_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {userData.user.email}
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 1 }} />

                            <MenuItem onClick={() => redirect('/profile')}>
                                <ListItemIcon><FontAwesomeIcon icon={faUser} color="#757575" /></ListItemIcon>
                                <Typography variant="body2" fontWeight="500">Mi Perfil</Typography>
                            </MenuItem>
                            
                            <MenuItem onClick={() => redirect('/security')}>
                                <ListItemIcon><FontAwesomeIcon icon={faShieldAlt} color="#757575" /></ListItemIcon>
                                <Typography variant="body2" fontWeight="500">Seguridad</Typography>
                            </MenuItem>

                            <MenuItem onClick={handleLanguage}>
                                <img src={i18n.language === 'es' ? en_language_img : es_language_img} alt='language' style={{ width: 20, marginRight: 15 }} />
                                <Typography variant="body2" fontWeight="500">{i18n.language === 'es' ? 'English' : 'Español'}</Typography>
                            </MenuItem>

                            <Divider sx={{ my: 1 }} />
                            
                            <MenuItem onClick={handleSignOut} sx={{ color: 'error.main' }}>
                                <ListItemIcon><ExitToAppIcon fontSize="small" color="error" /></ListItemIcon>
                                <Typography variant="body2" fontWeight="bold">Cerrar Sesión</Typography>
                            </MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* --- 2. DRAWER (Menu Izquierdo Persistente) --- */}
            <Drawer
                sx={{
                    width: DRAWER_WIDTH,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: DRAWER_WIDTH,
                        boxSizing: 'border-box',
                        borderRight: '1px solid #e0e0e0',
                        borderTop: 'none',
                    },
                }}
                variant="persistent"
                anchor="left"
                open={open}
            >
                <Toolbar sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    borderBottom: '1px solid #e0e0e0', 
                    px: 2 
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <img src={logoQUD} alt="QUD" style={{ height: 28, objectFit:'contain' }} />
                        <Typography variant="caption" sx={{ color: '#888', mt: 0.5, fontWeight: 'bold', letterSpacing: 0.5 }}>
                            PLATFORM
                        </Typography>
                    </Box>
                    <IconButton onClick={handleDrawerClose} sx={{ color: '#888' }}>
                        {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                    </IconButton>
                </Toolbar>

                {/* Contenido del Drawer */}
                <Box sx={{ 
                    overflowY: 'auto', 
                    flexGrow: 1, 
                    py: 2,
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none' 
                }}>
                    <List sx={{ px: 2 }}>
                        <AdminListItems onItemClick={handleMenuItemClick} />
                    </List>
                </Box>
                
                {/* Perfil Inferior en el Drawer */}
                <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', mt: 'auto' }}>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        p: 1.5, 
                        borderRadius: 3, 
                        bgcolor: '#f8f9fa' 
                    }}>
                        <Avatar 
                            src={imagesURL + 'empresas/' + userData.entity.profile_image} 
                            sx={{ bgcolor: theme.palette.primary.main, width: 36, height: 36, fontSize: 16 }}
                        >
                            {userData.entity.name ? userData.entity.name.charAt(0).toUpperCase() : 'Q'}
                        </Avatar>
                        <Box sx={{ ml: 2, overflow: 'hidden' }}>
                            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, color: '#333' }}>
                                {userData.entity.name || 'QUD'}
                            </Typography>
                            <Typography variant="caption" noWrap sx={{ color: '#888', display: 'block' }}>
                                Super Admin
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Drawer>

            {/* --- 3. MAIN CONTENT (Área de Vistas) --- */}
            <Main open={open}>
                {/* Spacer para que el contenido baje debajo del AppBar */}
                <Toolbar /> 

                <Container maxWidth="xl" sx={{ flexGrow: 1, mt: 2 }}>
                    {props.children}
                </Container>
                
                <Box sx={{ mt: 'auto' }}>
                    <Copyright sx={{ pt: 4, pb: 2 }} />
                </Box>
            </Main>
        </Box>
    );
}